import React, { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Map } from 'immutable';
import isEqual from 'lodash/isEqual';

import AlarmsToTriage, { ALARM_SEVERITIES, type AlarmsToTriageSegment } from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage';
import AreaFilter from 'src/screen/v2/Protected/DashboardScreen/AreaFilter';
import DeviceSummaryBar from 'src/screen/v2/Protected/DashboardScreen/DeviceSummaryBar';
import FacilityMap from 'src/screen/v2/Protected/DashboardScreen/FacilityMap';
import { fetchFacilitySummary } from 'src/module/device/action';
import { fetchTriageSummary } from 'src/module/alarm/action';
import { getClientId, getCriticalityLabels } from 'src/module/authentication/selector';
import { getDevicesSelectedFiltersObj } from 'src/module/filter/selector';
import { getFacilitySummary } from 'src/module/device/selector';
import { getTriageSummary, getTriageSummaryLoading } from 'src/module/alarm/selector';
import { isWhitelabel } from 'src/utils/utils';
import { pushHistory } from 'src/module/navigation/action';
import { setSelectedAttribute } from 'src/module/filter/action';
import { useTranslation } from 'src/i18n';


const DEVICE_LIST_PATH = '/devices/list';

export default function DashboardScreen () {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  const selectedFilters = useSelector(getDevicesSelectedFiltersObj, isEqual);
  const clientId = useSelector(getClientId);
  const criticalityLabels = useSelector(getCriticalityLabels);
  const summary = useSelector(getFacilitySummary);
  const triageSummary = useSelector(getTriageSummary);

  /* Only skeleton when there is nothing to show. A refetch (filter change,
   * refresh) keeps the previous counts up rather than flashing placeholders,
   * and `summary.isEmpty()` covers the first render, before `loading` is set. */
  const showSummarySkeletons = summary.isEmpty();

  /* The triage card tracks the fetch itself rather than an empty store: the
   * alarm slice is persisted, so on a warm load it shows the last session's
   * counts until the refetch dispatches. */
  const showTriageSkeletons = useSelector(getTriageSummaryLoading);

  /*
   * The pills set the attribute filter on the way out to the device list, so
   * coming back would leave it applied to a screen with no chip to show or
   * clear it -- and every future v2 component here would inherit it. The area
   * and unit filters are deliberately left alone; those persist by design.
   */
  useEffect(() => {
    dispatch(setSelectedAttribute(null));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchFacilitySummary(Map(selectedFilters)));
  }, [dispatch, clientId, selectedFilters]);

  useEffect(() => {
    dispatch(fetchTriageSummary(Map(selectedFilters)));
  }, [dispatch, clientId, selectedFilters]);

  /*
   * Drill in to the devices matching a pill. Both filters ride in redux: the
   * attribute is set here, and the area/unit filters are already there and are
   * not cleared on navigation, so the list opens scoped to both.
   */
  const openDeviceList = useCallback((attribute: string) => () => {
    dispatch(setSelectedAttribute(attribute));
    dispatch(pushHistory(DEVICE_LIST_PATH));
  }, [dispatch]);

  /*
   * The card shows three severity slots, but criticality is a per-client scale
   * of any depth (`preferences.criticalityLabels`). Take the three most severe
   * levels above 0 -- 0 means "not critical" and isn't triage-worthy -- and
   * label each with the client's own wording.
   */
  const segments: AlarmsToTriageSegment[] = useMemo(() => {
    const byCriticality = triageSummary.get('byCriticality', Map()) || Map();

    return ALARM_SEVERITIES.map((severity, idx) => {
      const criticality = criticalityLabels.size - 1 - idx;
      if (criticality < 1) return null;

      return {
        severity,
        count: byCriticality.get(String(criticality), 0) || 0,
        label: criticalityLabels.get(criticality) || `${criticality}`,
      };
    }).filter(Boolean) as AlarmsToTriageSegment[];
  }, [triageSummary, criticalityLabels]);

  return (
    <>
      <Helmet>
        <title>{title} - {t('dashboard.title')}</title>
      </Helmet>

      {/* The design has no visible page title, but the card headings below
        * need an <h1> above them to hang off. */}
      <h1 className='ds-sr-only'>{t('dashboard.title')}</h1>

      <div className='ds-relative'>
        <div className='ds-absolute ds-left-0 ds-right-0 ds-top-0 ds-z-10 ds-p-3.5'>
          <AreaFilter />
        </div>

        <FacilityMap>
          <DeviceSummaryBar
            loto={summary.get('loto')}
            offline={summary.get('offline')}
            alarming={summary.get('alarming')}
            totalDevices={summary.get('totalDevices')}
            forcedOn={summary.get('forcedOn')}
            onLotoClick={openDeviceList('loto')}
            onOfflineClick={openDeviceList('offline')}
            onAlarmingClick={openDeviceList('alarming')}
            onForcedOnClick={openDeviceList('forcedOn')}
            loading={showSummarySkeletons}
          />
        </FacilityMap>
      </div>

      {/* The map is full-bleed; everything below it is inset, per Figma. */}
      <div className='ds-flex ds-flex-col ds-gap-4 ds-p-4'>
        <AlarmsToTriage
          total={triageSummary.get('total', 0)}
          acknowledged={triageSummary.get('acknowledged', 0)}
          segments={segments}
          loading={showTriageSkeletons}
        />
      </div>
    </>
  );
}
